import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { employeeApi, getEmployeeData } from '../../lib/employeeApi'
import MobileLayout from '../../components/MobileLayout'
import { MessageCircle, Plus, ChevronRight } from 'lucide-react'

interface Conversation {
  partnerId: string
  partnerName: string
  lastMessage: { content: string | null; hasImage: boolean; createdAt: string; isMine: boolean }
  unreadCount: number
}

interface Employee { id: string; firstName: string; lastName: string; establishment: { id: string; name: string } }

function timeAgo(iso: string) {
  const d = new Date(iso)
  const diff = (Date.now() - d.getTime()) / 1000
  if (diff < 60) return 'À l\'instant'
  if (diff < 3600) return `${Math.floor(diff / 60)} min`
  if (diff < 86400) return `${Math.floor(diff / 3600)} h`
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
}

export default function MobileMessages() {
  const navigate = useNavigate()
  const emp = getEmployeeData()
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [employees, setEmployees] = useState<Employee[]>([])
  const [showNewModal, setShowNewModal] = useState(false)
  const [selectedEstabId, setSelectedEstabId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = async () => {
    try {
      const r = await employeeApi.get('/employee/me/messages')
      setConversations(r.data || [])
    } catch {} finally { setLoading(false) }
  }

  useEffect(() => {
    load()
    const interval = setInterval(load, 15000)
    return () => clearInterval(interval)
  }, [])

  const openNewConversation = async () => {
    try {
      const r = await employeeApi.get('/employee/me/messages/employees')
      setEmployees(r.data || [])
      setSelectedEstabId(null)
      setShowNewModal(true)
    } catch {}
  }

  return (
    <MobileLayout>
      <div className="flex flex-col min-h-full">
        {/* Titre + bouton nouveau */}
        <div className="bg-white border-b px-4 py-3 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <MessageCircle size={20} className="text-blue-600" />
            <span className="font-bold text-gray-900">Messagerie</span>
          </div>
          <button onClick={openNewConversation}
            className="flex items-center gap-1.5 bg-blue-600 text-white text-sm font-semibold px-3 py-1.5 rounded-full">
            <Plus size={16} /> Nouveau
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Chargement...</div>
        ) : conversations.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 px-8 text-center">
            <MessageCircle size={48} className="text-gray-200" />
            <p className="text-gray-400 text-sm">Aucune conversation</p>
            <button onClick={openNewConversation}
              className="bg-blue-600 text-white text-sm font-semibold px-5 py-2.5 rounded-full">
              Envoyer un message
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {conversations.map(c => (
              <button key={c.partnerId} onClick={() => navigate(`/app/messages/${c.partnerId}`)}
                className="w-full flex items-center gap-3 px-4 py-3.5 bg-white hover:bg-gray-50 transition-colors text-left">
                {/* Avatar */}
                <div className="w-11 h-11 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-base flex-shrink-0">
                  {c.partnerName.split(' ').map(n => n[0]).slice(0, 2).join('')}
                </div>
                {/* Contenu */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${c.unreadCount > 0 ? 'font-bold text-gray-900' : 'font-semibold text-gray-700'}`}>
                      {c.partnerName}
                    </span>
                    <span className="text-xs text-gray-400 ml-2 flex-shrink-0">{timeAgo(c.lastMessage.createdAt)}</span>
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <p className={`text-xs truncate ${c.unreadCount > 0 ? 'text-gray-800 font-medium' : 'text-gray-400'}`}>
                      {c.lastMessage.isMine ? 'Vous : ' : ''}
                      {c.lastMessage.hasImage && !c.lastMessage.content ? '📷 Photo' : (c.lastMessage.content || '📷 Photo')}
                    </p>
                    {c.unreadCount > 0 && (
                      <span className="ml-2 flex-shrink-0 w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">
                        {c.unreadCount > 9 ? '9+' : c.unreadCount}
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight size={16} className="text-gray-300 flex-shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Modal — étape 1 : choisir le magasin */}
      {showNewModal && (() => {
        const estabs = Array.from(new Map(employees.map(e => [e.establishment.id, e.establishment])).values())
        const filtered = selectedEstabId ? employees.filter(e => e.establishment.id === selectedEstabId) : []
        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-sm rounded-3xl max-h-[80vh] flex flex-col shadow-2xl">
              <div className="flex items-center justify-between px-5 py-4 border-b">
                {selectedEstabId ? (
                  <button onClick={() => setSelectedEstabId(null)} className="flex items-center gap-1 text-blue-600 font-semibold text-sm">
                    ← {estabs.find(e => e.id === selectedEstabId)?.name}
                  </button>
                ) : (
                  <span className="font-bold text-gray-900">Choisir un magasin</span>
                )}
                <button onClick={() => setShowNewModal(false)} className="text-gray-400 text-2xl leading-none">×</button>
              </div>

              <div className="overflow-y-auto flex-1">
                {/* Étape 1 : liste des magasins */}
                {!selectedEstabId && (
                  estabs.length === 0 ? (
                    <p className="text-center text-gray-400 text-sm py-8">Aucun employé disponible</p>
                  ) : estabs.map(estab => {
                    const count = employees.filter(e => e.establishment.id === estab.id).length
                    return (
                      <button key={estab.id} onClick={() => setSelectedEstabId(estab.id)}
                        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 border-b border-gray-100">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center text-blue-700 text-lg">🏪</div>
                          <span className="text-sm font-semibold text-gray-800">{estab.name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-400">
                          <span className="text-xs">{count} collègue{count > 1 ? 's' : ''}</span>
                          <ChevronRight size={16} />
                        </div>
                      </button>
                    )
                  })
                )}

                {/* Étape 2 : liste des collègues du magasin */}
                {selectedEstabId && (
                  filtered.length === 0 ? (
                    <p className="text-center text-gray-400 text-sm py-8">Aucun collègue dans ce magasin</p>
                  ) : filtered.map(e => (
                    <button key={e.id} onClick={() => { setShowNewModal(false); navigate(`/app/messages/${e.id}`) }}
                      className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50 border-b border-gray-100">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm flex-shrink-0">
                        {e.firstName[0]}{e.lastName && e.lastName !== '-' ? e.lastName[0] : ''}
                      </div>
                      <span className="text-sm font-medium text-gray-800">
                        {e.firstName} {e.lastName && e.lastName !== '-' ? e.lastName : ''}
                      </span>
                      <ChevronRight size={16} className="text-gray-300 ml-auto" />
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        )
      })()}
    </MobileLayout>
  )
}
